/**
 * Modal Component
 * Simple modal wrapper for dialogs and forms
 */

import { type ReactNode, type RefObject, useEffect, useCallback, useRef, useId } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: 'small' | 'medium' | 'large';
  /** ARIA role of the dialog; use 'alertdialog' for a prompt that needs a decision. */
  role?: 'dialog' | 'alertdialog';
  /** Element focused on open (e.g. the safe action of an alertdialog); defaults to the close button. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Omit the corner close button when the dialog's own actions already cover dismissal. */
  hideCloseButton?: boolean;
  /** Render a grab handle above the header at phone width, where the modal is a bottom sheet. */
  showGrabHandle?: boolean;
  'data-testid'?: string;
}

// Module-level stack of currently-open Modal instance ids, most-recently-opened last.
// Modals have no portal — a ConfirmDialog opened from inside another Modal is literally
// nested in the DOM — and every open Modal attaches its own document-level keydown
// listener. Without this, a single Escape (or Tab) keypress fires every open modal's
// handler at once, so closing a nested confirmation also closes the modal underneath it.
// Gating each handler on "am I the topmost open modal?" scopes the keypress to the one
// the user is actually looking at.
const openModalStack: string[] = [];

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  role = 'dialog',
  initialFocusRef,
  hideCloseButton = false,
  showGrabHandle = false,
  'data-testid': dataTestId,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);
  // Unique per instance — a hardcoded id breaks aria-labelledby (duplicate DOM ids resolve to
  // the wrong element) when a ConfirmDialog/Modal opens while another Modal is already open.
  const titleId = useId();

  // Save the currently focused element before the modal opens so we can restore it
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      // Move focus into the modal after render
      const raf = requestAnimationFrame(() => {
        (initialFocusRef?.current ?? closeButtonRef.current)?.focus();
      });
      return () => cancelAnimationFrame(raf);
    } else {
      // Restore focus to the trigger element when the modal closes
      if (triggerRef.current && triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
      triggerRef.current = null;
    }
  }, [isOpen, initialFocusRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Only the topmost open modal should react to keyboard input — a nested
      // modal (e.g. a ConfirmDialog opened from inside another Modal) sits on
      // top of it in the DOM and visually, so it alone should see the keypress.
      if (openModalStack[openModalStack.length - 1] !== titleId) return;

      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => el.offsetParent !== null); // exclude hidden elements

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose, titleId]
  );

  // Tracks this modal's place in the open-modal stack. Kept in its own effect, keyed only
  // on [isOpen, titleId] — NOT on handleKeyDown/onClose — so an unrelated re-render that
  // hands this modal a new onClose reference can't bump it back to "topmost" ahead of a
  // modal opened after it.
  useEffect(() => {
    if (!isOpen) return;
    openModalStack.push(titleId);
    document.body.style.overflow = 'hidden';
    return () => {
      const idx = openModalStack.indexOf(titleId);
      if (idx !== -1) openModalStack.splice(idx, 1);
      // Only lift the scroll lock once no modal is left open — an inner modal
      // closing shouldn't re-enable page scroll while an outer one is still open.
      if (openModalStack.length === 0) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, titleId]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center pb-[calc(var(--bottom-tab-height)+env(safe-area-inset-bottom,0px))] md:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="bg-card w-full md:max-w-lg md:rounded-lg rounded-t-2xl max-h-[90vh] overflow-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={dataTestId}
      >
        {showGrabHandle && (
          <div className="md:hidden flex justify-center pt-2" aria-hidden="true" data-testid="modal-grab-handle">
            <div className="h-1 w-10 rounded-full bg-muted-foreground/40" />
          </div>
        )}
        <div className="flex justify-between items-center px-5 py-3 border-b border-border">
          <h2 id={titleId} className="m-0 text-lg font-semibold text-foreground">
            {title}
          </h2>
          {!hideCloseButton && (
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="w-8 h-8 text-xl p-0 bg-transparent border-none cursor-pointer text-muted-foreground flex items-center justify-center rounded hover:bg-accent min-h-[44px] min-w-[44px]"
              aria-label="Close modal"
              data-testid="modal-close-button"
            >
              ×
            </button>
          )}
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export default Modal;
